<script setup>
import { ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Input from 'dashboard/components-next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const store = useStore();
const { t } = useI18n();

const url = ref('');
const isSaving = ref(false);

watch(
  () => props.inbox.webhook_forward_targets,
  saved => {
    url.value = saved?.[0]?.url || '';
  },
  { immediate: true }
);

const save = async () => {
  isSaving.value = true;
  try {
    const name = props.inbox.webhook_forward_targets?.[0]?.name || 'forward';
    await store.dispatch('inboxes/updateInbox', {
      id: props.inbox.id,
      formData: false,
      channel: {
        webhook_forward_targets: url.value ? [{ name, url: url.value }] : [],
      },
    });
    useAlert(t('INBOX_MGMT.ADD.LINE_CHANNEL.WEBHOOK_FORWARDING.SUCCESS'));
  } catch (error) {
    useAlert(t('INBOX_MGMT.ADD.LINE_CHANNEL.WEBHOOK_FORWARDING.ERROR'));
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col w-full gap-4 text-left">
    <div
      class="flex items-center justify-between gap-2 text-sm font-medium text-center"
    >
      <span class="flex-1 px-3 py-2 rounded-lg bg-n-teal-3 text-n-teal-11">
        {{ t('INBOX_MGMT.ADD.LINE_CHANNEL.WEBHOOK_FORWARDING.FLOW_LINE') }}
      </span>
      <span class="i-lucide-arrow-right size-4 text-n-slate-10" />
      <span class="flex-1 px-3 py-2 rounded-lg bg-n-iris-3 text-n-iris-11">
        {{ t('INBOX_MGMT.ADD.LINE_CHANNEL.WEBHOOK_FORWARDING.FLOW_HERE') }}
      </span>
      <span class="i-lucide-arrow-right size-4 text-n-slate-10" />
      <span class="flex-1 px-3 py-2 rounded-lg bg-n-alpha-2 text-n-slate-11">
        {{ t('INBOX_MGMT.ADD.LINE_CHANNEL.WEBHOOK_FORWARDING.FLOW_OTHER') }}
      </span>
    </div>
    <div class="flex items-center gap-2">
      <Input
        v-model="url"
        class="flex-1"
        :placeholder="
          t('INBOX_MGMT.ADD.LINE_CHANNEL.WEBHOOK_FORWARDING.URL_PLACEHOLDER')
        "
      />
      <NextButton
        faded
        blue
        :is-loading="isSaving"
        :label="t('INBOX_MGMT.ADD.LINE_CHANNEL.WEBHOOK_FORWARDING.SAVE')"
        @click="save"
      />
    </div>
    <p class="mb-0 text-xs text-n-slate-10">
      {{ t('INBOX_MGMT.ADD.LINE_CHANNEL.WEBHOOK_FORWARDING.HINT') }}
    </p>
  </div>
</template>
